/**
 * bg-replace.js — «люди на новом фоне»: the ONE place a video frame becomes its
 * subject over a colour, a blur or a picture.
 *
 * WHY A MODULE (24.09.2026). /video-bg-remover did this inline — the matte, the
 * kept object, the edge controls and the compositor were four calls strung
 * together in its pipeline — and a visitor of /remove-object-from-video asked
 * for the same thing there, in one sentence: «Убрать с заднего плана всё
 * лишнее, сделать фон серым». A second copy of that string of calls is how two
 * tools drift apart (the recon engine's house rule: one engine, two front
 * doors). Both tools now go through this file:
 *   · /video-bg-remover — two passes: stage.matte() into its matte store, the
 *     brush editor in between, compose() on export;
 *   · /remove-object-from-video — one pass: stage.frame() inside its render,
 *     AFTER the marked objects are rebuilt (recon-pipeline `postFrame`), and
 *     stage.still() for the before/after wipe.
 *
 * THE CONTRACT is the compositor's: a matte is an RGBA canvas, RGB = straight
 * foreground, A = alpha. RobustVideoMatting fills RGB with its decontaminated
 * foreground estimate, which is why a hair edge does not carry the old room's
 * colour onto the new background.
 *
 * ABOVE 1920 PX the matting net sees a downscaled copy (MATTE_MAX is the
 * ceiling /video-bg-remover's whole pipeline has always had), and the frame is
 * composed from its OWN pixels under the upscaled alpha: a 4K picture keeps its
 * sharpness; what it gives up is the decontamination of the edge band only.
 *
 * Public API — window.BgReplace:
 *   PALETTE                        backdrops the tools offer, gray first
 *   backdrop(id, opts) → bg|null   {mode, color, blurRel} for a palette id;
 *                                  'custom' takes opts.color, 'blur' opts.strength
 *   blurPx(rel, w, h) → px         a relative blur on a concrete frame
 *   compose(matte, bg, source, edge?) → canvas   edge controls, then composite
 *   subjectShare(rgba) → 0..1      share of the frame the subject covers
 *   createStage({engine, chroma, keep}) → stage
 *     stage.ready(signal)          load the model (no-op for the colour key)
 *     stage.begin()                a new clip: recurrent state and stats reset
 *     stage.matte(frame, t)        sequential matte, kept object unioned in
 *     stage.still(frame, t)        one-off matte for a preview; state untouched
 *     stage.frame(frame, t, bg, edge)  matte + compose, sequential
 *     stage.stats()                {frames, ms, kept, share}
 *     stage.provider()             'webgpu' | 'wasm' | 'chroma' | null
 */
(function (root) {
    'use strict';

    var MATTE_MAX = 1920;

    /* Gray first: it is what was asked for, and a flat mid-gray is the one
       backdrop that reads as «studio» under any skin tone and any exposure. The
       green is the broadcast chroma green (#00b140), for a person who will key
       the result again in an editor. */
    var PALETTE = [
        { id: 'gray', mode: 'color', color: '#808080' },
        { id: 'white', mode: 'color', color: '#ffffff' },
        { id: 'black', mode: 'color', color: '#000000' },
        { id: 'green', mode: 'color', color: '#00b140' },
        { id: 'blur', mode: 'blur' }
    ];

    /* A blur has to look the same on the preview canvas (screen size) and in
       the export (output size), so it is held as a share of the SHORT side and
       turned into pixels on the frame it is drawn on. Strength 1..10 maps to
       0.4 %..4 % of the short side: 5 → 14 px on a 720-wide portrait clip. */
    var BLUR_PER_STEP = 0.004;

    function backdrop(id, opts) {
        opts = opts || {};
        if (id === 'custom') {
            var c = String(opts.color || '');
            return /^#[0-9a-f]{6}$/i.test(c) ? { id: 'custom', mode: 'color', color: c.toLowerCase() } : null;
        }
        for (var i = 0; i < PALETTE.length; i++) {
            var p = PALETTE[i];
            if (p.id !== id) continue;
            if (p.mode === 'blur') {
                var s = Math.max(1, Math.min(10, +opts.strength || 5));
                return { id: 'blur', mode: 'blur', blurRel: s * BLUR_PER_STEP };
            }
            return { id: p.id, mode: 'color', color: p.color };
        }
        return null;
    }

    function blurPx(rel, w, h) {
        return Math.max(1, Math.round((+rel || 0) * Math.min(w || 0, h || 0)));
    }

    /* Share of the frame the subject covers: pixels at least half opaque. The
       page reads it to say «no people in this frame» instead of letting a
       fully-gray preview speak for itself. */
    function subjectShare(rgba) {
        if (!rgba || !rgba.length) return 0;
        var n = rgba.length >> 2, on = 0;
        for (var i = 3; i < rgba.length; i += 4) if (rgba[i] >= 128) on++;
        return n ? on / n : 0;
    }

    function makeCanvas(w, h) {
        var c = root.document.createElement('canvas');
        c.width = w; c.height = h;
        return c;
    }
    function even(n) { n = Math.max(2, Math.round(n)); return n % 2 ? n - 1 : n; }

    function compose(matte, bg, source, edge) {
        var E = root.VbrMatteEdge;
        var m = (edge && E && !E.isIdentity(edge)) ? E.apply(matte, edge) : matte;
        var b = bg;
        if (bg && bg.mode === 'blur' && bg.blurRel && !bg.blurRadius) {
            b = Object.assign({}, bg, { blurRadius: blurPx(bg.blurRel, m.width, m.height) });
        }
        return root.VbrCompositor.composite(m, b, source || null);
    }

    /* The source's own pixels under an alpha matted on a smaller copy:
       `destination-in` keeps the destination's colour and multiplies its alpha
       by the drawn matte's — one GPU draw each, no pixel loop. */
    function sourceUnderAlpha(frame, smallMatte) {
        var W = frame.width, H = frame.height;
        var c = makeCanvas(W, H), x = c.getContext('2d');
        x.drawImage(frame, 0, 0, W, H);
        x.globalCompositeOperation = 'destination-in';
        x.imageSmoothingEnabled = true;
        x.drawImage(smallMatte, 0, 0, W, H);
        x.globalCompositeOperation = 'source-over';
        return c;
    }

    function shareOf(canvas) {
        try {
            var w = canvas.width, h = canvas.height;
            // a 1/4-scale read is plenty for a share and 16× cheaper
            var sw = Math.max(1, w >> 2), sh = Math.max(1, h >> 2);
            var c = makeCanvas(sw, sh), x = c.getContext('2d', { willReadFrequently: true });
            x.drawImage(canvas, 0, 0, sw, sh);
            return subjectShare(x.getImageData(0, 0, sw, sh).data);
        } catch (_) { return null; }
    }

    function createStage(opts) {
        opts = opts || {};
        var engine = opts.engine === 'chroma' ? 'chroma' : 'ai';
        var chroma = opts.chroma || null;
        var keep = opts.keep || null;
        var small = null;
        var st = { frames: 0, ms: 0, kept: 0, shares: [] };
        function now() { return (root.performance && root.performance.now) ? root.performance.now() : Date.now(); }

        function rvm() {
            var R = root.VbrRvmEngine;
            if (!R) throw new Error('bg-replace: VbrRvmEngine missing');
            return R;
        }

        async function run(frame, stateless) {
            if (engine === 'chroma') return root.VbrChroma.matte(frame, chroma);
            var R = rvm();
            var W = frame.width, H = frame.height, long = Math.max(W, H);
            if (long <= MATTE_MAX) return stateless ? R.matteStateless(frame) : R.matte(frame);
            var k = MATTE_MAX / long, sw = even(W * k), sh = even(H * k);
            if (!small) small = makeCanvas(sw, sh);
            if (small.width !== sw || small.height !== sh) { small.width = sw; small.height = sh; }
            small.getContext('2d').drawImage(frame, 0, 0, sw, sh);
            var ms = stateless ? await R.matteStateless(small) : await R.matte(small);
            return sourceUnderAlpha(frame, ms);
        }

        function mergeKeep(m, frame, t) {
            if (!keep || typeof keep.active !== 'function' || !keep.active()) return false;
            try { return !!keep.mergeInto(m, frame, t); } catch (_) { return false; }
        }

        async function matte(frame, t) {
            var t0 = now();
            var m = await run(frame, false);
            if (mergeKeep(m, frame, t)) st.kept++;
            st.frames++;
            st.ms += now() - t0;
            // the subject's share, sampled — a per-frame read would cost more
            // than it tells
            if (st.frames % 12 === 1) { var s = shareOf(m); if (s != null) st.shares.push(s); }
            return m;
        }

        return {
            ready: function (signal) {
                if (engine === 'chroma') return Promise.resolve(true);
                return rvm().ready(signal);
            },
            begin: function () {
                st = { frames: 0, ms: 0, kept: 0, shares: [] };
                if (engine !== 'chroma') rvm().resetState();
            },
            matte: matte,
            still: async function (frame, t) {
                var m = await run(frame, true);
                mergeKeep(m, frame, t);
                return m;
            },
            // a callback-safe method: it is handed to the render as a bare function
            frame: async function (frame, t, bg, edge) {
                return compose(await matte(frame, t), bg, frame, edge);
            },
            stats: function () {
                var sh = st.shares, avg = null;
                if (sh.length) { var s = 0; for (var i = 0; i < sh.length; i++) s += sh[i]; avg = s / sh.length; }
                return { frames: st.frames, ms: Math.round(st.ms), msPerFrame: st.frames ? Math.round(st.ms / st.frames) : 0, kept: st.kept, share: avg };
            },
            provider: function () {
                if (engine === 'chroma') return 'chroma';
                var R = root.VbrRvmEngine;
                return (R && typeof R.provider === 'function') ? R.provider() : null;
            },
            shareOf: shareOf
        };
    }

    root.BgReplace = {
        PALETTE: PALETTE,
        MATTE_MAX: MATTE_MAX,
        backdrop: backdrop,
        blurPx: blurPx,
        compose: compose,
        subjectShare: subjectShare,
        shareOf: shareOf,
        createStage: createStage
    };
})(typeof window !== 'undefined' ? window : globalThis);
